import { useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export function OrderPage() {
  const salesinContainerRef = useRef(null);

  useEffect(() => {
    // Create an iframe approach for better isolation
    const container = salesinContainerRef.current;
    if (!container) return;

    // Create iframe for SalesIn portal
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.minHeight = '400px';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    
    // Build the SalesIn login page URL
    iframe.src = 'https://b2b.salesin.com/?company=IZKJBYIYn/WN27K3mQDyRg==';
    
    container.innerHTML = '';
    container.appendChild(iframe);

    // Adjust iframe height based on content
    iframe.onload = () => {
      try {
        iframe.style.height = '500px';
      } catch (e) {
        // Cross-origin restrictions may prevent height adjustment
      }
    };

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-[#01195d] pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Order Online
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Log in to access your account, view your pricing, and place orders directly.
          </p>
        </div>
      </section>

      {/* SalesIn Portal Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Customer Portal</h2>
              <p className="text-slate-600">
                Enter your credentials to access the B2B ordering portal
              </p>
            </div>
            
            {/* SalesIn Portal Container */}
            <div 
              ref={salesinContainerRef} 
              className="salesin-portal min-h-[400px] flex items-center justify-center"
            >
              <div className="text-center text-slate-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#01195d] mx-auto mb-4"></div>
                <p>Loading ordering portal...</p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-sm text-slate-500 text-center">
                Don't have an account?{' '}
                <a href="/trade-application" className="text-[#01195d] font-medium hover:underline">
                  Apply for a trade account
                </a>
              </p>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-2">Need help with your order?</p>
            <p className="text-slate-800">
              Call us at <a href="tel:1800934114" className="text-[#01195d] font-semibold">1800 934 114</a>
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-12">
            Benefits of Ordering Online
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#01195d]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#01195d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Your Pricing</h3>
              <p className="text-slate-600 text-sm">See your negotiated prices and special deals in real-time</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#01195d]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#01195d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Order History</h3>
              <p className="text-slate-600 text-sm">View past orders and quickly reorder your regular items</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#01195d]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#01195d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">24/7 Access</h3>
              <p className="text-slate-600 text-sm">Place orders anytime, anywhere at your convenience</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default OrderPage;
